import { useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  connectorOrigin,
  listConnections,
  revokeConnection,
  type Connection,
} from "@/stores/connectors/repository";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

function useConnections() {
  const { userId } = useAuth();
  const { t } = useLanguage();
  const copy = t.connectors;
  const client = useQueryClient();
  const [selected, setSelected] = useState<Connection | null>(null);
  const [status, setStatus] = useState("");
  const queryKey = ["connector-connections", userId, connectorOrigin()];
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => listConnections(pageParam, signal),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    enabled: Boolean(userId && connectorOrigin()),
    retry: false,
    gcTime: 0,
  });
  const mutation = useMutation({
    mutationFn: revokeConnection,
    onSuccess: async () => {
      setSelected(null);
      setStatus(copy.revokedSuccess);
      await client.invalidateQueries({ queryKey });
    },
  });
  const connections = query.data?.pages.flatMap((page) => page.connections) ?? [];
  return { query, mutation, selected, setSelected, status, connections };
}

type ConnectionModel = ReturnType<typeof useConnections>;

export function ConnectedApps() {
  const { t } = useLanguage();
  const copy = t.connectors;
  const model = useConnections();
  const { query, status, connections } = model;

  if (!connectorOrigin()) return <p role="status">{copy.unavailable}</p>;
  if (query.isPending) return <p role="status">{copy.loading}</p>;
  return (
    <div className="space-y-6">
      <p role="status" className="text-sm text-muted-foreground">
        {status}
      </p>
      {query.isError && (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertDescription>{copy.failed}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => void query.refetch()}>
            {copy.retry}
          </Button>
        </div>
      )}
      {!query.isError && connections.length === 0 && <p>{copy.empty}</p>}
      <ul className="divide-y divide-border">
        {connections.map((connection) => (
          <ConnectionRow key={connection.id} connection={connection} model={model} />
        ))}
      </ul>
      <MoreConnections model={model} />
      <RevokeConnectionDialog model={model} />
    </div>
  );
}

function ConnectionRow({ connection, model }: { connection: Connection; model: ConnectionModel }) {
  const { t, language } = useLanguage();
  const copy = t.connectors;
  const { mutation, setSelected } = model;
  const date = (value: string) =>
    new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value)
    );
  return (
    <li
      key={connection.id}
      className="flex flex-col items-start gap-4 py-6 first:pt-0 sm:flex-row sm:justify-between"
    >
      <div className="min-w-0 space-y-2 break-words">
        <h2 className="font-semibold">{connection.client_name || copy.unnamed}</h2>
        <p className="text-sm">{connection.organization_name ?? connection.organization}</p>
        <ul className="text-sm text-muted-foreground">
          {connection.scopes.map((scope) => (
            <li key={scope}>{copy.scopes[scope]}</li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {copy.created}: {date(connection.created_at)}
        </p>
        <p className="text-xs text-muted-foreground">
          {copy.lastUsed}:{" "}
          {connection.last_used_at ? date(connection.last_used_at) : copy.neverUsed}
        </p>
        {connection.revoked_at && <p className="text-sm">{copy.revoked}</p>}
      </div>
      {!connection.revoked_at && (
        <Button
          {...pageInfoTarget("connections.revoke")}
          variant="outline"
          className="shrink-0"
          onClick={() => {
            mutation.reset();
            setSelected(connection);
          }}
        >
          {copy.revoke}
        </Button>
      )}
    </li>
  );
}

function RevokeConnectionDialog({ model }: { model: ConnectionModel }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { selected, setSelected, mutation } = model;
  return (
    <AlertDialog
      open={selected !== null}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) setSelected(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.revokeTitle}</AlertDialogTitle>
          <AlertDialogDescription>
            {selected?.client_name || copy.unnamed} — {copy.revokeDescription}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {mutation.isError && (
          <Alert variant="destructive">
            <AlertDescription>{copy.failed}</AlertDescription>
          </Alert>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>{copy.cancel}</AlertDialogCancel>
          <AlertDialogAction
            disabled={mutation.isPending}
            onClick={(event) => {
              event.preventDefault();
              if (selected) mutation.mutate(selected.id);
            }}
          >
            {mutation.isPending ? copy.pending : copy.revoke}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MoreConnections({ model }: { model: ConnectionModel }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { query } = model;
  return (
    <>
      {query.hasNextPage && (
        <Button
          variant="outline"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? copy.pending : copy.more}
        </Button>
      )}
    </>
  );
}
